// components/ChartOfAccountsSearch.js
import React, { useState, useEffect, useCallback, useRef, useImperativeHandle } from "react";
import chartOfAccountsAPI from "../api/chartOfAccountsAPI";
import AddAccountModal from "./AddAccountModal";
import { useAuth } from "../context/AuthContext";

// Module-level shared cache — survives across all instances on the page.
// Key = lowercased trimmed query string. Value = array of account objects.
const sharedSearchCache = new Map();

// Track in-flight search promises so multiple instances asking for the
// same query share one request instead of firing N parallel ones.
const inflightSearches = new Map(); // key → Promise<results>

// Module-level cache for popular accounts. Fetched once per page session.
let popularAccountsCache = null;
let popularAccountsPromise = null;

async function fetchPopularAccountsOnce(popularTerms) {
  if (popularAccountsCache) return popularAccountsCache;
  if (popularAccountsPromise) return popularAccountsPromise;

  popularAccountsPromise = (async () => {
    try {
      const results = await Promise.all(
        popularTerms.map((term) =>
          chartOfAccountsAPI.search(term).catch(() => null)
        )
      );

      const merged = [];
      for (const res of results) {
        if (!res) continue;
        const data =
          (res && res.success && Array.isArray(res.data) && res.data) ||
          (Array.isArray(res) && res) ||
          (Array.isArray(res?.data) && res.data) ||
          [];
        if (data.length) merged.push(...data.slice(0, 2));
      }

      const unique = merged.reduce((acc, a) => {
        if (!acc.find((x) => x._id === a._id) && acc.length < 8) acc.push(a);
        return acc;
      }, []);

      popularAccountsCache = unique;
      return unique;
    } catch (err) {
      console.error('Failed to load popular accounts:', err);
      popularAccountsCache = [];
      return [];
    } finally {
      popularAccountsPromise = null;
    }
  })();

  return popularAccountsPromise;
}

export function clearChartOfAccountsCache() {
  sharedSearchCache.clear();
  popularAccountsCache = null;
  popularAccountsPromise = null;
}

/**
 * Requires your API to send context headers (storeId/accountingClientId) via axios interceptor.
 * Backend route: GET /chart-of-accounts/search?q=  (scoped by requireContext)
 */
const ChartOfAccountsSearch = React.forwardRef((
  {
    value = "",
    onChange,
    onSelect,
    onSearchResults, // optional
    placeholder = "Search chart of accounts...",
    disabled = false,
    className = "",
    showLinkedBadge = true,
    clearTrigger = false,
    popularTerms = ["cash", "accounts payable", "rent", "utilities", "office supplies"], // ✅ configurable
    clientId = null, // explicit accountingClientId — overrides localStorage context for create/validate
    // Hide accounts with postingAllowed:false (parent/summary accounts). Off by
    // default so pickers that legitimately select a parent are unaffected; transaction
    // line pickers should turn it on so the picker and the server agree on what is
    // selectable.
    postableOnly = false,

    // ── Opt-in generic-picker mode (WHT payee / ATC fields). Every default
    // below keeps the chart-of-accounts behaviour exactly as it was. ──────
    //
    // optionSource: search something other than the chart of accounts.
    //   { search: async (query) => items,         // '' = the "popular" list
    //     getKey: (item) => string,
    //     getDisplayValue: (item) => string,      // input text once picked
    //     renderOption: (item) => ReactNode,      // dropdown row content
    //     noun: 'payee' }                         // "Found 3 payee(s)…"
    // When set: no COA requests, no popular accounts, no context check, no
    // "Create new account".
    optionSource = null,
    // Pick-only: typed text is never a value. Editing after a pick clears the
    // selection (onSelect(null)); unmatched text shows noMatchMessage.
    strict = false,
    noMatchMessage = "No matching option",
    // Controlled selection for strict pickers (undefined = uncontrolled, the
    // COA default). The item, or null.
    selectedOption = undefined,
    // ArrowUp/ArrowDown move the highlight, Enter picks it, Escape closes.
    keyboardSelect = false,
    inputId,
    ariaLabel,
    invalid = false,
  },
  ref
) => {
  const usingSource = Boolean(optionSource);
  const [highlighted, setHighlighted] = useState(0);
  // Set when this component itself emitted onSelect(null) while the user is
  // typing, so the controlled-selection sync below doesn't wipe their text.
  const selfClearedRef = useRef(false);
  const sourceSeqRef = useRef(0);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // Hide accounts the GL will refuse a posting to. Parent/summary accounts (MRWD marks
  // every "-00" parent postingAllowed:false — data/seedCOA_MRWD.js) were previously
  // offered here and rejected only on save; worse, an exact name match sorts first, so
  // searching "Electricity Expenses" put the unpostable parent ABOVE its postable
  // children.
  //
  // Applied here rather than before sharedSearchCache.set(): that cache is shared by
  // every instance on the page and keyed by query text alone, so filtering upstream
  // would leak one picker's policy into every other picker's results.
  const applyPostableFilter = useCallback(
    (list) => (postableOnly ? (list || []).filter((a) => a.postingAllowed !== false) : (list || [])),
    [postableOnly]
  );
  const [popularAccounts, setPopularAccounts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { isAuthReady, isAuthenticated, isRefreshRevoked } = useAuth();

  const popularTermsRef = useRef(popularTerms);

  const suggestionRef = useRef(null);
  const inputRef = useRef(null);

  // ✅ Expose inputRef to parent via forwardRef
  useImperativeHandle(ref, () => ({
    inputRef,
  }), []);


  // ✅ Context availability check:
  // Prefer these keys (matching your context middleware usage in FE interceptor).
  // If you use different keys, adjust them here.
  const storeId = localStorage.getItem("storeId");
  const accountingClientId = localStorage.getItem("accountingClientId");
  const isContextAvailable = usingSource || !!(storeId || accountingClientId);

  useEffect(() => {
    if (value && !selectedAccount) setQuery(value);
  }, [value, selectedAccount]);

  useEffect(() => {
    if (selectedOption === undefined) return;
    if (selectedOption) {
      setSelectedAccount(selectedOption);
      setQuery(optionSource ? optionSource.getDisplayValue(selectedOption) : `${selectedOption.accountNumber} - ${selectedOption.accountName}`);
    } else {
      setSelectedAccount(null);
      if (selfClearedRef.current) selfClearedRef.current = false;
      else setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (clearTrigger) clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTrigger]);

  const formatAccountTypeLabel = (account) => {
    const t = account?.accountType;
    if (!t) return "";
    if (typeof t === "string") return t; // fallback
    // populated object
    const name = t.name || "";
    const code = t.code ? ` (${t.code})` : "";
    return `${name}${code}`.trim();
  };

  useEffect(() => {
    if (usingSource) return;
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) return;
    if (!isContextAvailable) return;
    let cancelled = false;
    fetchPopularAccountsOnce(popularTermsRef.current).then((accounts) => {
      if (!cancelled) setPopularAccounts(accounts);
    });
    return () => { cancelled = true; };
  }, [isAuthReady, isAuthenticated, isRefreshRevoked, isContextAvailable]);

  const searchChartOfAccounts = useCallback(
    async (searchQuery) => {
      if (optionSource) {
        const seq = ++sourceSeqRef.current;
        setSearching(true);
        try {
          const items = (await optionSource.search(searchQuery.trim())) || [];
          if (seq === sourceSeqRef.current) {
            setSuggestions(items);
            setHighlighted(0);
            onSearchResults?.({ query: searchQuery, count: items.length });
          }
        } catch {
          if (seq === sourceSeqRef.current) setSuggestions([]);
        } finally {
          if (seq === sourceSeqRef.current) setSearching(false);
        }
        return;
      }
      if (!isAuthReady || !isAuthenticated || isRefreshRevoked) return;
      if (!isContextAvailable) {
        setSuggestions([]);
        setShowSuggestions(false);
        onSearchResults?.({ query: searchQuery, count: 0 });
        return;
      }

      if (!searchQuery.trim()) {
        setSuggestions(applyPostableFilter(popularAccounts));
        onSearchResults?.({ query: "", count: popularAccounts.length });
        return;
      }

      const cacheKey = searchQuery.toLowerCase().trim();

      // 1. Cache hit — return immediately.
      if (sharedSearchCache.has(cacheKey)) {
        const cached = sharedSearchCache.get(cacheKey) || [];
        setSuggestions(applyPostableFilter(cached));
        onSearchResults?.({ query: searchQuery, count: cached.length });
        return;
      }

      // 2. In-flight for the same key — wait for the existing promise.
      if (inflightSearches.has(cacheKey)) {
        try {
          setSearching(true);
          const refined = await inflightSearches.get(cacheKey);
          setSuggestions(applyPostableFilter(refined));
          onSearchResults?.({ query: searchQuery, count: refined.length });
        } finally {
          setSearching(false);
        }
        return;
      }

      // 3. Cold path — fire the request, share the promise.
      const fetchPromise = (async () => {
        try {
          const res = await chartOfAccountsAPI.search(searchQuery);
          let searchResults = [];

          if (res && res.success) searchResults = res.data || [];
          else if (Array.isArray(res)) searchResults = res;
          else if (res && Array.isArray(res.data)) searchResults = res.data;

          const term = searchQuery.toLowerCase();

          const refined = (searchResults || []).filter((a) => {
            const n = (a.accountName || "").toLowerCase();
            const num = (a.accountNumber || "").toLowerCase();
            const t = formatAccountTypeLabel(a).toLowerCase();
            const d = (a.description || "").toLowerCase();
            return n.includes(term) || num.includes(term) || t.includes(term) || d.includes(term);
          });

          refined.sort((a, b) => {
            const aName = (a.accountName || "").toLowerCase();
            const bName = (b.accountName || "").toLowerCase();
            const aNum = (a.accountNumber || "").toLowerCase();
            const bNum = (b.accountNumber || "").toLowerCase();
            const q = term;
            const aExact = aName === q || aNum === q;
            const bExact = bName === q || bNum === q;
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            const aStarts = aName.startsWith(q) || aNum.startsWith(q);
            const bStarts = bName.startsWith(q) || bNum.startsWith(q);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return aNum.localeCompare(bNum);
          });

          sharedSearchCache.set(cacheKey, refined);
          return refined;
        } catch (err) {
          console.error("Chart of accounts search failed:", err);
          return [];
        } finally {
          inflightSearches.delete(cacheKey);
        }
      })();

      inflightSearches.set(cacheKey, fetchPromise);

      try {
        setSearching(true);
        const refined = await fetchPromise;
        setSuggestions(applyPostableFilter(refined));
        onSearchResults?.({ query: searchQuery, count: refined.length });
      } finally {
        setSearching(false);
      }
    },
    [isAuthReady, isAuthenticated, isRefreshRevoked, isContextAvailable, popularAccounts, onSearchResults, optionSource]
  );

  // debounce search
  useEffect(() => {
    if (!query.trim()) return;
    const t = setTimeout(() => searchChartOfAccounts(query), 200);
    return () => clearTimeout(t);
  }, [query, searchChartOfAccounts, isFocused]);

  const handleAccountSelect = useCallback(
    (account) => {
      const displayValue = optionSource
        ? optionSource.getDisplayValue(account)
        : `${account.accountNumber} - ${account.accountName}`;
      setSelectedAccount(account);
      setQuery(displayValue);
      setShowSuggestions(false);
      setIsFocused(false);

      onSelect?.(account);
    },
    [onSelect, optionSource]
  );

  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setQuery(newValue);
      if (strict && selectedAccount) {
        // A pick-only field: editing the text un-picks the option.
        selfClearedRef.current = selectedOption !== undefined;
        onSelect?.(null);
      }
      setSelectedAccount(null);

      if (usingSource) {
        // After a pick the input keeps focus but isFocused is false, so typing
        // again would show nothing (the COA field's long-standing behaviour,
        // left as is). Pickers re-open on any keystroke; '' lists everything.
        setIsFocused(true);
        setShowSuggestions(true);
        if (!newValue.trim()) searchChartOfAccounts("");
      } else if (newValue.trim() && isFocused) setShowSuggestions(true);
      onChange?.(newValue);
    },
    [onChange, isFocused, strict, selectedAccount, selectedOption, onSelect, usingSource, searchChartOfAccounts]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!keyboardSelect) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setShowSuggestions(true);
        setHighlighted((i) => {
          const n = suggestions.length;
          if (!n) return 0;
          return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
        });
      } else if (e.key === "Enter") {
        if (showSuggestions && suggestions.length) {
          e.preventDefault();
          handleAccountSelect(suggestions[Math.min(highlighted, suggestions.length - 1)]);
        }
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [keyboardSelect, suggestions, showSuggestions, highlighted, handleAccountSelect]
  );

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
    setShowSuggestions(true);
    if (!query.trim()) searchChartOfAccounts("");
  }, [query, searchChartOfAccounts]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedAccount(null);
    setQuery("");
    setShowSuggestions(false);
    setIsFocused(false);
    onChange?.("");
    onSelect?.(null);
    onSearchResults?.({ query: "", count: popularAccounts.length });
  }, [onChange, onSelect, onSearchResults, popularAccounts.length]);

  const contextLabel = storeId ? "Store" : accountingClientId ? "Accounting Client" : "Context";

  return (
    <div className={`space-y-2 ${showSuggestions && isFocused ? 'mb-96' : ''}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={keyboardSelect ? handleKeyDown : undefined}
          id={inputId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-autocomplete={usingSource ? "list" : undefined}
          disabled={disabled || !isContextAvailable}
          className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
            disabled ? "bg-gray-100 text-gray-500" : ""
          } ${invalid ? "border-danger-500" : ""} ${className}`}
          placeholder={!isContextAvailable ? "COA search not available (missing context)" : placeholder}
        />

        {searching && isFocused && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
          </div>
        )}

        {selectedAccount && !disabled && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {isContextAvailable && showSuggestions && isFocused && (
          <div
            ref={suggestionRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto top-full"
            style={{
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            {searching ? (
              <div className="px-4 py-3 text-center">
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                  <span className="text-sm">Searching accounts...</span>
                </div>
              </div>
            ) : suggestions.length > 0 ? (
              <>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between sticky top-0">
                  <div className="text-xs font-medium text-gray-500">
                    {usingSource
                      ? (query.trim()
                        ? `Found ${suggestions.length} ${optionSource.noun || "option"}(s) for "${query}"`
                        : `${suggestions.length} ${optionSource.noun || "option"}(s)`)
                      : query.trim()
                        ? `Found ${suggestions.length} account(s) for "${query}"`
                        : `Popular Accounts (${suggestions.length})`}
                  </div>
                  {!usingSource && <div className="text-[10px] text-gray-400">{contextLabel} Context</div>}
                </div>

                {suggestions.map((account, index) => (
                  <div
                    key={usingSource ? optionSource.getKey(account) : account._id}
                    role={usingSource ? "option" : undefined}
                    aria-selected={usingSource ? index === highlighted : undefined}
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 transition-colors duration-150 ${
                      keyboardSelect && index === highlighted ? "bg-gray-100" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAccountSelect(account);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {usingSource ? optionSource.renderOption(account) : (
                      <>
                        <div className="font-medium text-gray-900">{account.accountName}</div>
                        <div className="text-sm text-gray-500">
                          {account.accountNumber}
                          {formatAccountTypeLabel(account) ? ` • ${formatAccountTypeLabel(account)}` : ""}
                          {account.description ? ` • ${account.description}` : ""}
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {!usingSource && query.trim() && (
                  <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 sticky bottom-0">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowCreateModal(true)}
                      className="w-full text-sm text-teal-600 font-medium hover:underline text-center py-1"
                    >
                      + Create new account
                    </button>
                  </div>
                )}
              </>
            ) : query.trim() && usingSource ? (
              <div className="px-4 py-3 text-center text-sm text-gray-500">{noMatchMessage}</div>
            ) : query.trim() ? (
              <div className="px-4 py-3 text-center space-y-2">
                <div className="text-sm text-gray-500">
                  No accounts found for "{query}"
                </div>

                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowCreateModal(true)}
                  className="text-sm text-teal-600 font-medium hover:underline"
                >
                  + Create new account
                </button>
              </div>
            ) : (
              <div className="px-4 py-2 text-gray-500 text-center text-sm">
                {usingSource ? `Type to search ${optionSource.noun || "option"}s` : "Type to search chart of accounts"}
              </div>
            )}
          </div>
        )}
      </div>

      <AddAccountModal
        isOpen={!usingSource && showCreateModal}
        initialName={query}
        clientId={clientId || accountingClientId || null}
        onClose={() => setShowCreateModal(false)}
        onCreated={(account) => {
          handleAccountSelect(account); // ⭐ auto select newly created
        }}
      />


      {strict && !selectedAccount && query.trim() && !isFocused && !disabled && (
        <p role="alert" className="text-xs text-danger-500">{noMatchMessage}</p>
      )}

      {showLinkedBadge && selectedAccount && (
        <div className="text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded flex items-center">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Linked to Chart of Account
        </div>
      )}

      {!isContextAvailable && (
        <div className="text-xs text-orange-600">
          COA search not available — missing context (storeId or accountingClientId)
        </div>
      )}
    </div>
  );
});

ChartOfAccountsSearch.displayName = 'ChartOfAccountsSearch';

export default ChartOfAccountsSearch;
